/**
 * Alarms the device raises itself (tri-iot `FIRE_EXTINGUISHER_EVENTS`).
 *
 * The device sends an alarm as a measurement of that name with the value 1, so a rule "alarm
 * greater than 0" fires the moment the alarm arrives — and on nothing else: not on the hourly
 * readings, not on an old alarm value. That is what "forward every device alarm" means, without
 * the threshold guessing of acceleration or pressure.
 *
 * The rule stays an ordinary comparison list (one comparison, or one `OrCondition` of them), so the
 * server needs no new rule type; this module only recognises that shape so the editor can show the
 * alarms as choices instead of "[Gerät bewegt sich] [größer als] [0]" rows.
 */

/** Alarms that are rare on a healthy device — offered up front. */
export const DEVICE_ALARMS_MAIN = [
    'deviceMoving',
    'deviceOverturned',
    'sensorFault',
    'capTampered',
    'hoseTampered',
] as const;

/**
 * Alarms that have a threshold rule of their own. `pressureLow` is set on every reading while the
 * pressure is low (9,451 times in three days on 12 devices), so a rule on it is a flood; these sit
 * behind "more alarms".
 */
export const DEVICE_ALARMS_MORE = ['pressureLow', 'pressureHigh', 'temperatureAlarm'] as const;

export const DEVICE_ALARMS_ALL: readonly string[] = [...DEVICE_ALARMS_MAIN, ...DEVICE_ALARMS_MORE];

/** What the "device alarm" preset starts with. */
export const DEVICE_ALARMS_DEFAULT: readonly string[] = ['deviceMoving', 'deviceOverturned', 'sensorFault'];

/**
 * Wait time of the preset (24 hours, product decision 2026-10-26): a device in a lasting state repeats its
 * alarm with every reading (sensor fault hourly, "moved" every 15 minutes), and every SMS counts against
 * the daily limit. A second alarm of the same device within it is not reported again.
 */
export const DEVICE_ALARM_COOLDOWN_MINUTES = 24 * 60;

const comparison = (field: string) => ({
    conditionType: 'Comparison',
    field,
    operator: 'greater',
    comparisonType: 'value',
    comparisonValue: 0,
});

/** The conditions for a set of alarms, in the order of {@link DEVICE_ALARMS_ALL}. */
export const deviceAlarmConditions = (alarms: readonly string[]): any[] => {
    const ordered = DEVICE_ALARMS_ALL.filter((alarm) => alarms.includes(alarm));

    return ordered.length <= 1
        ? ordered.map(comparison)
        : [{ conditionType: 'OrCondition', conditions: ordered.map(comparison) }];
};

const isAlarmComparison = (condition: any): boolean =>
    !!condition
    && DEVICE_ALARMS_ALL.includes(condition.field)
    && condition.operator === 'greater'
    && (condition.comparisonType === undefined || condition.comparisonType === 'value')
    && Number(condition.comparisonValue) === 0
    && condition.comparisonValue !== ''
    && condition.comparisonValue !== null;

/**
 * The alarms of a rule that is exactly "any of these device alarms", or `null` for every other
 * shape (a threshold, an extra condition, a group inside the group) — those stay ordinary rows.
 */
export const deviceAlarmsOf = (conditions: any[] | undefined | null): string[] | null => {
    const list = conditions ?? [];

    if (list.length !== 1) {
        return null;
    }

    const [only] = list;
    const children = only?.conditionType === 'OrCondition' && Array.isArray(only.conditions)
        ? only.conditions
        : [only];

    if (children.length === 0 || !children.every(isAlarmComparison)) {
        return null;
    }

    const found = new Set<string>(children.map((child: any) => child.field));

    return DEVICE_ALARMS_ALL.filter((alarm) => found.has(alarm));
};
